/**
 * Batched XP-gem presentation for the Arena.
 *
 * The Arena can hold 120 replicated gems at once.
 * The original renderer expanded every gem into a Container plus four to eight
 * Graphics children, so late waves carried more than a thousand gem display
 * objects through Pixi's scene traversal and batch packing.
 * Gem collection and cap-merging also changed that scene tree on nearly every
 * snapshot.
 *
 * This module keeps the authored scatter, bob, tier colour, glow, and absorb
 * choreography while storing every visible mote as a lightweight Particle in
 * one ParticleContainer.
 * Particles are data records rather than display objects, so the whole ground
 * field contributes one stable object to the world scene graph.
 */
import { Particle, ParticleContainer, Texture } from 'pixi.js';

/** How long the scatter-drop plays, per orb, before its stagger. */
export const ORB_DROP_MS = 380;

/** Per-orb stagger on the drop, so the pile pours out instead of teleporting. */
export const ORB_DROP_STAGGER_MS = 45;

/** Idle bob period; each orb rides its own phase so the pile twinkles. */
const BOB_PERIOD_MS = 1400;

/** Idle bob amplitude, px. */
const BOB_AMPLITUDE_PX = 1.6;

/**
 * How a gem spends render frames.
 *
 * - `animated`: relayout every frame.
 * - `coarse`: relayout at most every {@link ORB_COARSE_LAYOUT_MS}.
 * - `static`: settle once and hold.
 */
export type XpOrbCadence = 'animated' | 'coarse' | 'static';

/** Idle relayout interval under the `coarse` cadence, roughly 12 Hz. */
export const ORB_COARSE_LAYOUT_MS = 80;

/** One value tier of gem: how many orbs, how big, and what colour they draw. */
interface OrbTier {
  /** Base XP at or above which this tier applies. */
  min: number;
  count: number;
  radius: number;
  color: number;
}

/**
 * Value tiers, low to high.
 * Thresholds sit against the bestiary's tier XP curve.
 */
const ORB_TIERS: readonly OrbTier[] = [
  { min: 0, count: 4, radius: 3.4, color: 0x8cf03c },
  { min: 15, count: 5, radius: 3.8, color: 0x3cd0f0 },
  { min: 45, count: 6, radius: 4.2, color: 0xb870ff },
  { min: 120, count: 8, radius: 5, color: 0xffd24d },
];

/** Pick the tier a base-XP value renders as. */
export function orbTierFor(value: number): OrbTier {
  let tier = ORB_TIERS[0]!;
  for (const candidate of ORB_TIERS) if (value >= candidate.min) tier = candidate;
  return tier;
}

/** Tiny deterministic PRNG so a gem's scatter is stable per entity id. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Three lightweight particles preserve one orb's halo, body, and glint. */
interface OrbParticles {
  halo: Particle;
  body: Particle;
  glint: Particle;
  ox: number;
  oy: number;
  phase: number;
  delay: number;
  radius: number;
}

/**
 * One stable batched field.
 *
 * Position, scale, and colour are dynamic because drop and absorb animation
 * updates them.
 * Rotation and UVs never change after particle creation.
 */
export class XpOrbLayer extends ParticleContainer<Particle> {
  private readonly freeByTier = new Map<number, XpOrbSprite[]>();

  constructor(zIndex: number) {
    super({
      texture: Texture.WHITE,
      dynamicProperties: {
        position: true,
        vertex: true,
        color: true,
        rotation: false,
        uvs: false,
      },
    });
    this.eventMode = 'none';
    this.zIndex = zIndex;
  }

  create(value: number, seed: number): XpOrbSprite {
    const tier = orbTierFor(value);
    const free = this.freeByTier.get(tier.min)?.pop();
    if (free) {
      free.recycle(value, seed);
      return free;
    }
    return new XpOrbSprite(this, value, seed);
  }

  /** Return a hidden handle to its same-tier free list without editing the particle buffer. */
  release(sprite: XpOrbSprite, tierMin: number): void {
    const free = this.freeByTier.get(tierMin);
    if (free) free.push(sprite);
    else this.freeByTier.set(tierMin, [sprite]);
  }
}

/**
 * Lightweight handle for one replicated gem's particles.
 *
 * This is deliberately not a Pixi Container.
 * Moving, animating, or disposing a gem edits its particle records inside the
 * shared layer without adding or removing a display object from the world.
 */
export class XpOrbSprite {
  readonly color: number;

  private readonly layer: XpOrbLayer;
  private readonly tierMin: number;
  private readonly orbs: OrbParticles[] = [];
  private readonly particles: Particle[] = [];
  private value: number;
  private seed: number;
  private ageMs = 0;
  private absorbP = 0;
  private alpha = 1;
  private cadence: XpOrbCadence = 'animated';
  private sinceLayoutMs = 0;
  private originX = 0;
  private originY = 0;
  private disposed = false;

  constructor(layer: XpOrbLayer, value: number, seed: number) {
    this.layer = layer;
    const tier = orbTierFor(value);
    this.tierMin = tier.min;
    this.color = tier.color;
    this.value = value;
    this.seed = seed;
    const rand = mulberry32(seed || 1);

    for (let i = 0; i < tier.count; i += 1) {
      const angle = rand() * Math.PI * 2;
      const dist = 5 + rand() * 12;
      const common = {
        texture: Texture.WHITE,
        anchorX: 0.5,
        anchorY: 0.5,
        rotation: Math.PI / 4,
      };
      const halo = new Particle({
        ...common,
        tint: tier.color,
        alpha: 0,
      });
      const body = new Particle({
        ...common,
        tint: tier.color,
        alpha: 0,
      });
      const glint = new Particle({
        ...common,
        tint: 0xffffff,
        alpha: 0,
      });
      this.orbs.push({
        halo,
        body,
        glint,
        ox: Math.cos(angle) * dist,
        oy: Math.sin(angle) * dist * 0.5,
        phase: rand() * BOB_PERIOD_MS,
        delay: i * ORB_DROP_STAGGER_MS,
        radius: tier.radius,
      });
      this.particles.push(halo, body, glint);
    }

    layer.addParticle(...this.particles);
    this.layout();
  }

  /** Reactivate a same-tier pooled handle for a new replicated entity id. */
  recycle(value: number, seed: number): void {
    this.value = value;
    this.seed = seed;
    this.ageMs = 0;
    this.absorbP = 0;
    this.alpha = 1;
    this.cadence = 'animated';
    this.sinceLayoutMs = 0;
    this.originX = 0;
    this.originY = 0;
    this.disposed = false;

    const tier = orbTierFor(value);
    const rand = mulberry32(seed || 1);
    for (let index = 0; index < this.orbs.length; index += 1) {
      const orb = this.orbs[index]!;
      const angle = rand() * Math.PI * 2;
      const dist = 5 + rand() * 12;
      orb.ox = Math.cos(angle) * dist;
      orb.oy = Math.sin(angle) * dist * 0.5;
      orb.phase = rand() * BOB_PERIOD_MS;
      orb.delay = index * ORB_DROP_STAGGER_MS;
      orb.radius = tier.radius;
      orb.halo.tint = tier.color;
      orb.body.tint = tier.color;
      orb.glint.tint = 0xffffff;
    }
    this.layout();
  }

  /** Number of lightweight particles owned by this gem. */
  get particleCount(): number {
    return this.particles.length;
  }

  /** Current screen-space origin, used if a removal lands before its position cache. */
  get position(): Readonly<{ x: number; y: number }> {
    return { x: this.originX, y: this.originY };
  }

  /** Number of currently visible particles, useful for presentation assertions. */
  get visibleParticleCount(): number {
    return this.particles.filter((particle) => particle.alpha > 0).length;
  }

  /**
   * Stable pose signature for cadence tests and diagnostics.
   * This does not expose mutable particle records to callers.
   */
  poseKey(): string {
    return this.orbs
      .map(({ body }) => `${body.x},${body.y},${body.scaleX},${body.alpha}`)
      .join('|');
  }

  setPosition(x: number, y: number): void {
    if (x === this.originX && y === this.originY) return;
    this.originX = x;
    this.originY = y;
    this.layout();
  }

  setAlpha(alpha: number): void {
    this.alpha = Math.min(1, Math.max(0, alpha));
    this.layout();
  }

  /** Move this gem between batches through pooled target storage. */
  transferTo(layer: XpOrbLayer): XpOrbSprite {
    if (layer === this.layer || this.disposed) return this;
    const transferred = layer.create(this.value, this.seed);
    transferred.ageMs = this.ageMs;
    transferred.absorbP = this.absorbP;
    transferred.alpha = this.alpha;
    transferred.cadence = this.cadence;
    transferred.sinceLayoutMs = this.sinceLayoutMs;
    transferred.originX = this.originX;
    transferred.originY = this.originY;
    transferred.layout();
    this.dispose();
    return transferred;
  }

  /** Absorb progress in the inclusive range 0 to 1. */
  setAbsorb(progress: number): void {
    this.absorbP = Math.min(1, Math.max(0, progress));
    this.layout();
  }

  setCadence(cadence: XpOrbCadence): void {
    if (cadence === this.cadence) return;
    this.cadence = cadence;
    this.sinceLayoutMs = 0;
    if (cadence === 'static') this.settle();
    else this.layout();
  }

  /** Advance the scatter and bob clock. */
  update(dtMs: number): void {
    this.ageMs += dtMs;
    if (this.cadence === 'static') return;
    if (this.cadence === 'coarse') {
      this.sinceLayoutMs += dtMs;
      if (this.sinceLayoutMs < ORB_COARSE_LAYOUT_MS) return;
      this.sinceLayoutMs = 0;
    }
    this.layout();
  }

  private settle(): void {
    const dropEndMs = ORB_DROP_MS + this.orbs.length * ORB_DROP_STAGGER_MS;
    if (this.ageMs < dropEndMs) this.ageMs = dropEndMs;
    this.layout();
  }

  private layout(): void {
    if (this.disposed) return;
    const contract = 1 - easeInQuad(Math.min(1, this.absorbP * 1.4));
    const shrink = 1 - 0.5 * easeInQuad(this.absorbP);
    const absorbAlpha = this.absorbP > 0.8 ? 1 - (this.absorbP - 0.8) / 0.2 : 1;

    for (const orb of this.orbs) {
      const elapsed = this.ageMs - orb.delay;
      const visibleAlpha = elapsed > 0 ? this.alpha * absorbAlpha : 0;
      const progress = elapsed <= 0 ? 0 : Math.min(1, elapsed / ORB_DROP_MS);
      const lateral = easeOutQuad(progress);
      const hop = Math.sin(Math.PI * progress) * -9;
      const bob =
        progress >= 1
          ? Math.sin(((this.ageMs + orb.phase) / BOB_PERIOD_MS) * Math.PI * 2) *
            BOB_AMPLITUDE_PX
          : 0;
      const x = this.originX + orb.ox * lateral * contract;
      const y =
        this.originY +
        (-14 * (1 - lateral) + orb.oy * lateral + hop + bob) * contract;
      const radius = orb.radius * shrink;

      setParticle(
        orb.halo,
        x,
        y,
        radius * 4.8,
        0.28 * visibleAlpha,
      );
      setParticle(
        orb.body,
        x,
        y,
        radius * 2,
        0.95 * visibleAlpha,
      );
      setParticle(
        orb.glint,
        x - radius * 0.3,
        y - radius * 0.35,
        radius * 0.8,
        0.9 * visibleAlpha,
      );
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const particle of this.particles) particle.alpha = 0;
    this.layer.release(this, this.tierMin);
  }
}

function setParticle(
  particle: Particle,
  x: number,
  y: number,
  size: number,
  alpha: number,
): void {
  particle.x = x;
  particle.y = y;
  particle.scaleX = size;
  particle.scaleY = size;
  particle.alpha = alpha;
}

function easeOutQuad(progress: number): number {
  return 1 - (1 - progress) * (1 - progress);
}

function easeInQuad(progress: number): number {
  return progress * progress;
}
